// Scores and improvement. Every score shows its band, its change against your recent average
// and how far it is from your target. Ink for the numbers and bars; green and red only on a change.
import { type Band, bandOf, SKILL_BANDS, SKILL_TARGET } from '@shared/scoring';
import { type ReactNode, useEffect, useState } from 'react';
import { cx } from '@/lib/cx';
import { reducedMotion } from '@/lib/motion';
import { Icon } from './Icon';

/** Counts from 0 up to `value` once, in about 0.6 s. Shows `value` at once when `run` is off or motion is reduced. */
function useCountUp(value: number, run: boolean): number {
  const [shown, setShown] = useState(() => (run && !reducedMotion() ? 0 : value));
  useEffect(() => {
    if (!run || reducedMotion()) {
      setShown(value);
      return;
    }
    let frame = 0;
    const start = performance.now();
    const step = (now: number) => {
      const t = Math.min(1, (now - start) / 600);
      setShown(Math.round(value * (1 - (1 - t) ** 3)));
      if (t < 1) frame = requestAnimationFrame(step);
    };
    frame = requestAnimationFrame(step);
    return () => cancelAnimationFrame(frame);
  }, [value, run]);
  return shown;
}

type ScoreProps = {
  value: number;
  of?: string;
  size?: 'sm' | 'lg';
  /** Count up from 0: once, when an answer has just been scored. */
  countUp?: boolean;
};

/** Big number: 74/100. `of` changes the unit, e.g. "% right". */
export function Score({ value, of = '/100', size = 'lg', countUp = false }: ScoreProps) {
  const shown = useCountUp(value, countUp);
  return (
    <div className={cx('score', size === 'sm' && 'sm')}>
      <span className="n" aria-hidden="true">
        {shown}
      </span>
      <span className="of" aria-hidden="true">
        {of}
      </span>
      <span className="sr-only">
        {value}
        {of}
      </span>
    </div>
  );
}

type DeltaProps = {
  value: number | null | undefined;
  /** Words after the number: "in 30 days", "vs your average". */
  text?: string;
  /** Lower is better (fillers, mistakes): a fall shows as good. */
  lowerIsBetter?: boolean;
  /** Decimal places, for rates like fillers per 100 words. */
  decimals?: number;
};

/** +3 in green, −2 in red, "same" in gray. Renders nothing without a value. */
export function Delta({ value, text, lowerIsBetter, decimals = 0 }: DeltaProps) {
  if (value === null || value === undefined || Number.isNaN(value)) return null;
  const better = lowerIsBetter ? value < 0 : value > 0;
  const worse = lowerIsBetter ? value > 0 : value < 0;
  const cls = better ? 'up' : worse ? 'down' : 'flat';
  const sign = value > 0 ? '+' : value < 0 ? '−' : '';
  const number = `${sign}${Math.abs(value).toFixed(decimals)}`;
  // Zero with words reads as just the words ("At your average"); zero alone reads "same".
  const label = value === 0 ? (text ?? 'same') : text ? `${number} ${text}` : number;
  return <span className={cx('delta', cls)}>{label}</span>;
}

type MeterProps = {
  value: number;
  target?: number | undefined;
  thin?: boolean;
  label?: string;
};

/** Ink bar from 0 to 100 with a target tick. Reaching the target turns the tick into a check. */
export function Meter({ value, target, thin, label }: MeterProps) {
  const width = Math.max(2, Math.min(100, value));
  const reached = target !== undefined && value >= target;
  return (
    <div
      className={cx('meter', thin && 'thin')}
      role="img"
      aria-label={
        label ?? `${value} out of 100${target === undefined ? '' : `, target ${target}${reached ? ', reached' : ''}`}`
      }
    >
      <span style={{ width: `${width}%` }} />
      {target !== undefined &&
        (reached ? (
          <i className="target reached" style={{ left: `${target}%` }}>
            <Icon name="check" />
          </i>
        ) : (
          <i className="target" style={{ left: `${target}%` }} />
        ))}
    </div>
  );
}

export type CriterionRow = { name: string; value: number; change?: number | null; target?: number };

/** The criterion furthest below its target: the one to work on. None when every criterion is on target. */
export function focusOf(rows: readonly CriterionRow[], target: number = SKILL_TARGET): string | undefined {
  let focus: string | undefined;
  let widest = 0;
  for (const r of rows) {
    const gap = (r.target ?? target) - r.value;
    if (gap > widest) {
      widest = gap;
      focus = r.name;
    }
  }
  return focus;
}

/** Name, bar, value, change, one row per criterion; in a narrow column the bar goes under the name. The weakest is
 * marked "Focus", unless `focus` is off (the screen's focus is something else). */
export function CritRows({
  rows,
  target = SKILL_TARGET,
  focus: marked = true,
}: {
  rows: readonly CriterionRow[];
  target?: number;
  focus?: boolean;
}) {
  const focus = marked ? focusOf(rows, target) : undefined;
  return (
    <div className="crit-rows">
      {rows.map((r) => (
        <div className="crit" key={r.name}>
          <span className="name">
            {r.name === focus ? <b>{r.name}</b> : r.name}
            {r.name === focus && <span className="chip">Focus</span>}
          </span>
          <Meter value={r.value} target={r.target ?? target} thin label={`${r.name}: ${r.value}`} />
          <span className="v">{r.value}</span>
          <Delta value={r.change} />
        </div>
      ))}
    </div>
  );
}

/** The band as a plain outlined label: "B2", or "Clear" for bands without a level. `about`: "About B2", for a level
 * estimated from one result rather than measured by an exam. */
export function BandChip({
  score,
  bands = SKILL_BANDS,
  about = false,
}: {
  score: number;
  bands?: readonly Band[];
  about?: boolean;
}) {
  const band = bandOf(score, bands);
  return (
    <span className="chip band" title={band.level ? band.label : undefined}>
      {band.level && about ? `About ${band.level}` : (band.level ?? band.label)}
    </span>
  );
}

type ScoreSummaryProps = {
  eyebrow: string;
  score: number;
  target?: number;
  bands?: readonly Band[];
  /** Your average of the last 5 before this one. */
  average?: number | null;
  /** The coach's one line: what this score means for you. */
  line?: ReactNode;
  /** Count the score up once (just scored). */
  countUp?: boolean;
  /** Criteria rows and notes under a hairline. */
  children?: ReactNode;
};

/** The top of every result: the score, its band, the change against your average, one bar to the target. */
export function ScoreSummary({
  eyebrow,
  score,
  target = SKILL_TARGET,
  bands = SKILL_BANDS,
  average,
  line,
  countUp,
  children,
}: ScoreSummaryProps) {
  const gap = target - score;
  return (
    <div className="score-summary">
      <p className="t-2 small">{eyebrow}</p>
      <div className="score-head">
        <Score value={score} countUp={countUp} />
        <div className="score-meta">
          <BandChip score={score} bands={bands} about />
          {average !== undefined && average !== null && (
            <Delta
              value={score - average}
              text={score === average ? `At your average (${average})` : `vs your average (${average})`}
            />
          )}
        </div>
      </div>
      <Meter value={score} target={target} />
      <p className="t-2 small">
        {gap > 0
          ? `${gap} ${gap === 1 ? 'point' : 'points'} to your target of ${target}`
          : `At or above your target of ${target}`}
      </p>
      {line && <p className="verdict">{line}</p>}
      {children && <div className="score-rows stack-sm">{children}</div>}
    </div>
  );
}
